import React from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useSidebar } from '@gu-corp/gu-ui';
import { Home, Wallet, Image as ImageIcon, Landmark, Menu } from 'lucide-react';
import { AppRoutesEnum } from '~/enums/route.enum';

/**
 * Bottom tab bar for mobile. The sidebar becomes an off-canvas sheet on small
 * screens, so this gives quick access to the primary destinations; the last
 * item opens the full sidebar sheet.
 */
const TABS: { href: string; labelKey: string; Icon: React.ElementType }[] = [
  { href: AppRoutesEnum.DASHBOARD, labelKey: 'home', Icon: Home },
  { href: AppRoutesEnum.WALLET, labelKey: 'wallet', Icon: Wallet },
  { href: AppRoutesEnum.NFTS, labelKey: 'nft', Icon: ImageIcon },
  { href: AppRoutesEnum.LENDING, labelKey: 'lending', Icon: Landmark },
];

export const MobileTabBar: React.FC = () => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { setOpenMobile } = useSidebar();

  const isActive = (href: string) =>
    href === AppRoutesEnum.DASHBOARD ? router.pathname === '/' : router.pathname.startsWith(href);

  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-border bg-background/95 backdrop-blur md:hidden">
      {TABS.map(({ href, labelKey, Icon }) => {
        const active = isActive(href);
        return (
          <Link key={href} href={href} passHref legacyBehavior>
            <a
              className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] ${
                active ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <Icon className="h-5 w-5" />
              {t(labelKey)}
            </a>
          </Link>
        );
      })}
      <button
        type="button"
        onClick={() => setOpenMobile(true)}
        className="flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] text-muted-foreground"
      >
        <Menu className="h-5 w-5" />
        {t('menu')}
      </button>
    </nav>
  );
};

export default MobileTabBar;
